"use client";

import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useState } from "react";
export default function Transformation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: timelineRef,
    isVisible: timelineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: progressRef,
    isVisible: progressVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const [progressValue, setProgressValue] = useState(0);
  useEffect(() => {
    if (progressVisible) {
      const timer = setTimeout(() => setProgressValue(70), 200);
      return () => clearTimeout(timer);
    }
  }, [progressVisible]);
  const steps = [{
    number: "01",
    title: "مشاوره اولیه",
    description: "در این مرحله، نیازها، سبک زندگی و اهداف شما برای بازطراحی فضای مسکونی یا تجاری را بررسی می‌کنیم.",
    duration: "۳۰ دقیقه"
  }, {
    number: "02",
    title: "تحلیل فضا",
    description: "تیم ما با بررسی دقیق ابعاد، نورپردازی و پتانسیل‌های موجود، طرحی متناسب با سلیقه شما ارائه می‌دهد.",
    duration: "۳ روز"
  }, {
    number: "03",
    title: "طراحی و برنامه‌ریزی",
    description: "انتخاب متریال، پالت رنگی و چیدمان نهایی با دقت بالا برای ایجاد فضایی منحصر به فرد انجام می‌شود.",
    duration: "۱ هفته"
  }, {
    number: "04",
    title: "اجرا و نظارت",
    description: "نظارت دقیق بر اجرای طرح توسط تیم متخصص ما برای تضمین کیفیت و هماهنگی با جزئیات طراحی.",
    duration: "۳۰ روز"
  }, {
    number: "05",
    title: "تحویل نهایی",
    description: "فضای شما آماده است؛ با پشتیبانی مستمر و اطمینان از رضایت کامل شما از نتیجه نهایی.",
    duration: "مداوم"
  }];
  return <section data-section-id="270" id="گامهای-تحول-فضا" className="relative overflow-hidden py-32 bg-background text-foreground">
      <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
        {}
        <div ref={headerRef} className={`max-w-3xl mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <p className="text-xs font-mono uppercase tracking-[0.2em] mb-6 text-primary">
            — مسیر تحول
          </p>
          <h2 className="font-default text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-6 text-foreground">
            از یک ایده ساده تا{" "}
            <em className="italic text-primary">
              فضایی رویایی.
            </em>
          </h2>
          <p className="font-default text-base lg:text-lg text-foreground/70 leading-relaxed max-w-2xl">
            سفری شفاف و برنامه‌ریزی شده برای تبدیل فضای شما به محیطی مدرن و شخصی‌سازی شده. بدون پیچیدگی، با تمرکز بر کیفیت و جزئیات.
          </p>
        </div>

        {}
        <div ref={timelineRef} className="relative">
          {}
          <div className="hidden lg:block absolute top-[50%] left-0 right-0 h-px bg-border" />

          {}
          <div className="lg:hidden absolute top-0 bottom-0 left-6 w-px bg-border" />

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-4 relative">
            {steps.map((step, i) => <div key={i} data-index={i} className={`relative transition-all duration-700 ease-out ${timelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 120}ms`
          }}>
                {}
                <div className="hidden lg:flex absolute top-[calc(50%-6px)] left-1/2 -translate-x-1/2 z-10">
                  <span className="block w-3 h-3 rounded-lg bg-primary shadow-md" />
                </div>

                {}
                <div className="lg:hidden absolute top-6 left-6 -translate-x-1/2 z-10">
                  <span className="block w-3 h-3 rounded-lg bg-primary shadow-md" />
                </div>

                <Card className="lg:mb-12 ml-14 lg:ml-0 p-6 rounded-lg border bg-card hover:bg-muted transition-colors duration-300 gap-0 shadow-lg">
                  <div className="font-default text-5xl mb-4 leading-none text-primary">
                    {step.number}
                  </div>
                  <h3 className="font-default text-xl mb-3 text-foreground leading-snug">
                    {step.title}
                  </h3>
                  <p className="font-default text-sm text-foreground/65 leading-relaxed mb-5 line-clamp-2">
                    {step.description}
                  </p>
                  <div className="pt-4 border-t border-border">
                    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-primary">
                      زمان: {step.duration}
                    </span>
                  </div>
                </Card>
              </div>)}
          </div>
        </div>

        {}
        <div ref={progressRef} className={`mt-24 max-w-2xl mx-auto transition-all duration-1000 ease-out ${progressVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
              میانگین زمان اجرا
            </span>
            <span className="font-default text-2xl text-foreground">۱۴ روز</span>
          </div>
          <Progress value={progressValue} className="h-1 bg-muted [&>[data-slot=progress-indicator]]:bg-primary transition-all duration-[1500ms] ease-out" />
          <p className="font-default text-xs text-foreground/50 mt-4 text-center">
            از اولین مشاوره تا تحویل نهایی، اکثر پروژه‌ها در کمتر از دو هفته به نتیجه مطلوب می‌رسند.
          </p>
        </div>
      </div>
    </section>;
}